import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  getWeeksInRange,
  getCurrentWeek,
  weekToDateRange,
} from '../utils/weekUtils';
import {
  CELL_WIDTH,
  ROW_HEIGHT,
  CELL_HEIGHT,
  BLOCK_MARGIN,
  WEEK_WINDOW_BUFFER,
} from '../utils/gridConstants';
import {
  createBlock,
  updateBlock,
  updateMultipleBlocks,
  deleteBlock,
  loadMoreWeeksPast,
  loadMoreWeeksFuture,
  WEEKS_TO_LOAD,
} from '../ducks/plannerActions';
import PlanBlock from './PlanBlock';
import GridCell from './presentational/GridCell';
import WeekHeader, { GridSpacer } from './presentational/WeekHeader';
import DragOverlayBlock from './presentational/DragOverlayBlock';
import useNetworkStatus from '../../../shared/utils/networkStatus';
import useBlockOverlaps from '../hooks/useBlockOverlaps';
import useBlockPositions from '../hooks/useBlockPositions';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useGridNavigation from '../hooks/useGridNavigation';
import usePlannerDnd from '../hooks/usePlannerDnd';

const PlanViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Nunito Sans', sans-serif;
  color: #444444;
  flex: 1;
  min-height: 0;
`;

const GridViewport = styled.div`
  width: 100%;
  overflow: auto;
  align-self: stretch;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0 18px 18px 18px;
  width: fit-content;
  margin-bottom: 48px;
  margin-top: 6px;
  position: relative;
`;

const CurrentWeekIndicator = styled.div`
  position: absolute;
  left: 0;
  top: -3px;
  bottom: 0;
  width: 3px;
  background: #ff0000;
  z-index: 20;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    background: #ff0000;
    border-radius: 50%;
    z-index: 20;
  }
`;

const GridBody = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 100%;
  border-radius: 0 0 18px 18px;
  overflow: hidden;
`;

const GridRow = styled.div`
  display: flex;
  min-height: ${ROW_HEIGHT}px;
  border-bottom: 1px solid #eeeeee;
  background-color: #ffffff;
`;

const EmptyGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(15, 23, 42, 0.6);
  font-size: 14px;
  font-weight: 500;
  padding: 60px 16px;
  text-align: center;
`;

const BlocksLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  padding-right: 12px;
  overflow: visible;

  > * {
    pointer-events: auto;
  }
`;

const LoadingIndicator = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #77a7ff;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${(props) => (props.show ? '1' : '0')};
  visibility: ${(props) => (props.show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::before {
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ScrollNavButton = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === 'left' ? 'left: 12px;' : 'right: 12px;')}
  width: 36px;
  height: 72px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  /* Only apply backdrop-filter on non-Safari browsers - Safari has major perf issues with it */
  @supports not (-webkit-touch-callout: none) {
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.95);
  }

  &:hover {
    background: #ffffff;
    border-color: #bdbdbd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.97);
    background: #f5f5f5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(119, 167, 255, 0.4),
      0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.7);
  }

  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: #666666;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.2s ease;
  }

  &:hover svg {
    stroke: #444444;
  }

  /* Hide on touch devices since they have native scrolling */
  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const OfflineBanner = styled.div`
  background: #e74c3c;
  color: white;
  padding: 12px 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  margin-bottom: 16px;

  &::before {
    content: '⚠️';
    font-size: 16px;
  }
`;

const PlanView = () => {
  const dispatch = useDispatch();
  const loadedWeekRange = useSelector(
    (state) => state.planner.ui.loadedWeekRange,
  );
  const blocksMap = useSelector((state) => state.planner.data.blocks);
  const blocks = useMemo(() => Object.values(blocksMap), [blocksMap]);

  const selectedCalendarId = useSelector(
    (state) => state.planner.ui.selectedCalendarId,
  );
  const isLoadingMoreWeeks = useSelector(
    (state) => state.planner.ui.isLoadingMoreWeeks,
  );

  const gridContainerRef = useRef(null);

  // Track when editing/resize ended to prevent click-through
  const editingEndTimeRef = useRef(0);
  const resizeEndTimeRef = useRef(0);

  // Multi-select: store selected block IDs as a Set for better performance
  const [selectedBlockIds, setSelectedBlockIds] = useState(new Set());
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [draggingBlocks, setDraggingBlocks] = useState({});

  // Detect Safari so we can skip custom wheel logic that hurts momentum scrolling there
  const isSafari = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  const { isOnline } = useNetworkStatus();
  const currentWeekString = getCurrentWeek();

  const weeks = useMemo(() => {
    if (loadedWeekRange.fromWeek && loadedWeekRange.toWeek) {
      return getWeeksInRange(loadedWeekRange.fromWeek, loadedWeekRange.toWeek);
    }
    return [];
  }, [loadedWeekRange.fromWeek, loadedWeekRange.toWeek]);

  const weekIndexMap = useMemo(() => {
    const map = new Map();
    weeks.forEach((week, index) => map.set(week, index));
    return map;
  }, [weeks]);

  // Use infinite scroll hook for scroll state management
  const {
    visibleWindow,
    canScrollLeft,
    canScrollRight,
    previousYear,
    startScrolling,
    stopScrolling,
    jumpToToday,
    jumpToTargetWeek,
  } = useInfiniteScroll({
    containerRef: gridContainerRef,
    weeks,
    weekIndexMap,
    currentWeekString,
    loadedWeekRange,
    isLoadingMoreWeeks,
    onLoadMorePast: (fromWeek) => dispatch(loadMoreWeeksPast(fromWeek)),
    onLoadMoreFuture: (toWeek) => dispatch(loadMoreWeeksFuture(toWeek)),
    weeksToLoad: WEEKS_TO_LOAD,
    isTouchDevice,
    isSafari,
  });

  const rows = useMemo(() => {
    const minRows = 8;
    const maxBlockRow = blocks.length > 0 ? Math.max(...blocks.map((b) => b.row || 1)) : 0;
    return Array.from(
      { length: Math.max(minRows, maxBlockRow + 2) },
      (_, i) => i + 1,
    );
  }, [blocks]);

  const visibleBlocks = useMemo(() => {
    if (visibleWindow.end < visibleWindow.start) return [];
    const windowStart = Math.max(0, visibleWindow.start - WEEK_WINDOW_BUFFER);
    const windowEnd = Math.min(
      weeks.length - 1,
      visibleWindow.end + WEEK_WINDOW_BUFFER,
    );

    return blocks.filter((block) => {
      const fromIndex = weekIndexMap.get(block.fromWeek);
      const toIndex = weekIndexMap.get(block.toWeek);
      if (fromIndex === undefined || toIndex === undefined) return false;
      return !(toIndex < windowStart || fromIndex > windowEnd);
    });
  }, [
    blocks,
    visibleWindow.start,
    visibleWindow.end,
    weekIndexMap,
    weeks.length,
  ]);

  const cellHasBlock = useMemo(() => {
    const lookup = new Set();
    if (visibleWindow.end < visibleWindow.start) {
      return lookup;
    }

    const windowStart = Math.max(0, visibleWindow.start - WEEK_WINDOW_BUFFER);
    const windowEnd = Math.min(
      weeks.length - 1,
      visibleWindow.end + WEEK_WINDOW_BUFFER,
    );

    visibleBlocks.forEach((block) => {
      const fromIndex = weekIndexMap.get(block.fromWeek);
      const toIndex = weekIndexMap.get(block.toWeek);
      if (fromIndex === undefined || toIndex === undefined) return;

      const clampedStart = Math.max(fromIndex, windowStart);
      const clampedEnd = Math.min(toIndex, windowEnd);
      for (let weekIdx = clampedStart; weekIdx <= clampedEnd; weekIdx += 1) {
        const week = weeks[weekIdx];
        if (week) {
          lookup.add(`${block.row}-${week}`);
        }
      }
    });
    return lookup;
  }, [
    visibleBlocks,
    weeks,
    weekIndexMap,
    visibleWindow.start,
    visibleWindow.end,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const coarseQuery = window.matchMedia
      ? window.matchMedia('(pointer: coarse)')
      : null;

    const updateTouchState = () => {
      const hasTouchPoints = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
      const coarsePointer = coarseQuery ? coarseQuery.matches : false;
      setIsTouchDevice(hasTouchPoints || coarsePointer);
    };

    updateTouchState();

    if (!coarseQuery) return undefined;

    const handleChange = () => updateTouchState();

    // Modern listeners
    if (coarseQuery.addEventListener) {
      coarseQuery.addEventListener('change', handleChange);
      return () => coarseQuery.removeEventListener('change', handleChange);
    }

    // Legacy listeners for older browsers without addEventListener support.
    // @ts-ignore legacy API is safe to call when present
    if (typeof coarseQuery.addListener === 'function') {
      // @ts-ignore legacy API is safe to call when present
      coarseQuery.addListener(handleChange);
    }

    return () => {
      // @ts-ignore legacy API is safe to call when present
      if (typeof coarseQuery.removeListener === 'function') {
        // @ts-ignore legacy API is safe to call when present
        coarseQuery.removeListener(handleChange);
      }
    };
  }, []);

  const handleEditingStart = useCallback((blockId) => {
    setEditingBlockId(blockId);
  }, []);

  const handleEditingEnd = useCallback(() => {
    setEditingBlockId(null);
    setSelectedBlockIds(new Set());
    // Record when editing ended (used to prevent click-through)
    editingEndTimeRef.current = Date.now();
  }, []);

  // block overlap computation
  const {
    getOverlappingBlocks,
    isRightmostOverlappingBlock,
  } = useBlockOverlaps(visibleBlocks, weekIndexMap);

  // For backward compatibility, get the first selected block ID
  const selectedBlockId = selectedBlockIds.size > 0
    ? Array.from(selectedBlockIds)[0]
    : null;

  // Keyboard navigation for accessibility
  useGridNavigation({
    blocks,
    selectedBlockId,
    editingBlockId,
    weekIndexMap,
    weeks,
    maxRows: rows.length,
    isOnline,
    onSelect: (blockId) => setSelectedBlockIds(new Set([blockId])),
    onDeselect: () => setSelectedBlockIds(new Set()),
    onStartEditing: handleEditingStart,
    onStopEditing: handleEditingEnd,
    onDelete: () => {
      // Delete all selected blocks
      Array.from(selectedBlockIds).forEach((id) => {
        dispatch(deleteBlock(id));
      });
      setSelectedBlockIds(new Set());
      setEditingBlockId(null);
    },
    onMove: (blockId, newCol, newRow, weekSpan) => {
      const fromWeek = weeks[newCol];
      const toWeekIndex = Math.min(newCol + weekSpan - 1, weeks.length - 1);
      const toWeek = weeks[toWeekIndex];
      const { startDate } = weekToDateRange(fromWeek);
      const { endDate } = weekToDateRange(toWeek);

      dispatch(updateBlock(blockId, {
        fromWeek,
        toWeek,
        startWeek: fromWeek,
        endWeek: toWeek,
        startDate,
        endDate,
        row: newRow,
      }));
    },
    containerRef: gridContainerRef,
  });

  // Pre-compute position props for each block
  const blockPositions = useBlockPositions(
    visibleBlocks,
    weekIndexMap,
    CELL_WIDTH,
    ROW_HEIGHT,
    CELL_HEIGHT,
  );

  // Drag handling callbacks for usePlannerDnd
  const handleMoveDuringDrag = useCallback(
    (blockId, newColIndex, newRow, weekSpan) => {
      if (newColIndex < 0 || newColIndex >= weeks.length) return;

      const fromWeek = weeks[newColIndex];
      const toWeekIndex = Math.min(newColIndex + weekSpan - 1, weeks.length - 1);
      const toWeek = weeks[toWeekIndex];
      const { startDate } = weekToDateRange(fromWeek);
      const { endDate } = weekToDateRange(toWeek);

      setDraggingBlocks({
        [blockId]: {
          fromWeek, toWeek, startDate, endDate, row: newRow,
        },
      });
    },
    [weeks],
  );

  const handleMoveComplete = useCallback(
    (blockId, newColIndex, newRow, weekSpan) => {
      if (newColIndex < 0 || newColIndex >= weeks.length) return;

      const movedBlock = blocks.find((b) => b.id === blockId);
      if (!movedBlock) return;

      const fromWeek = weeks[newColIndex];
      const toWeekIndex = Math.min(newColIndex + weekSpan - 1, weeks.length - 1);
      const toWeek = weeks[toWeekIndex];

      const positionChanged = fromWeek !== movedBlock.fromWeek
        || toWeek !== movedBlock.toWeek
        || newRow !== movedBlock.row;

      if (!positionChanged) {
        setTimeout(() => setDraggingBlocks({}), 50);
        return;
      }

      const { startDate } = weekToDateRange(fromWeek);
      const { endDate } = weekToDateRange(toWeek);

      // Handle row swaps - batch all updates together
      if (newRow !== movedBlock.row) {
        const finalCollidingBlocks = blocks.filter((block) => {
          if (block.id === blockId || block.row !== newRow) return false;
          const blockFromIndex = weekIndexMap.get(block.fromWeek);
          const blockToIndex = weekIndexMap.get(block.toWeek);
          if (blockFromIndex === undefined || blockToIndex === undefined) return false;
          return !(toWeekIndex < blockFromIndex || newColIndex > blockToIndex);
        });

        const blockUpdates = [
          {
            blockId,
            updates: {
              fromWeek,
              toWeek,
              startWeek: fromWeek,
              endWeek: toWeek,
              startDate,
              endDate,
              row: newRow,
            },
          },
          ...finalCollidingBlocks.map((collidingBlock) => ({
            blockId: collidingBlock.id,
            updates: { row: movedBlock.row },
          })),
        ];

        dispatch(updateMultipleBlocks(blockUpdates));
      } else {
        dispatch(updateBlock(blockId, {
          fromWeek,
          toWeek,
          startWeek: fromWeek,
          endWeek: toWeek,
          startDate,
          endDate,
          row: newRow,
        }));
      }

      setDraggingBlocks({});
      // Note: drag end timestamp is tracked in usePlannerDnd hook
    },
    [blocks, weeks, weekIndexMap, dispatch],
  );

  // Use @dnd-kit for drag and drop
  const {
    dndContextProps,
    justFinishedDragging,
    activeBlockId,
    activeBlockData,
  } = usePlannerDnd({
    weekIndexMap,
    weeksLength: weeks.length,
    maxRows: rows.length,
    cellWidth: CELL_WIDTH,
    rowHeight: ROW_HEIGHT,
    onMoveDuringDrag: handleMoveDuringDrag,
    onMoveComplete: handleMoveComplete,
    onSelect: (blockId) => setSelectedBlockIds(new Set([blockId])),
    isOnline,
  });

  // Get the active block for DragOverlay
  const activeBlock = activeBlockId ? blocksMap[activeBlockId] : null;

  const handleBlockColorChange = useCallback(
    (blockId, colorId) => {
      if (selectedCalendarId) {
        dispatch(updateBlock(blockId, { colorId }));
      }
    },
    [selectedCalendarId, dispatch],
  );

  const handleBlockResize = useCallback(
    (blockId, startColIndex, endColIndex) => {
      if (startColIndex < 0 || endColIndex >= weeks.length) return;

      const startWeek = weeks[startColIndex];
      const endWeek = weeks[endColIndex];
      const { startDate } = weekToDateRange(startWeek);
      const { endDate } = weekToDateRange(endWeek);

      dispatch(
        updateBlock(blockId, {
          fromWeek: startWeek,
          toWeek: endWeek,
          startWeek,
          endWeek,
          startDate,
          endDate,
        }),
      );

      // Record when resize ended (used to prevent click-through)
      resizeEndTimeRef.current = Date.now();
    },
    [weeks, dispatch],
  );

  const handleBlockNameChange = useCallback(
    (blockId, newName) => {
      dispatch(updateBlock(blockId, { name: newName }));
    },
    [dispatch],
  );

  /**
   * Handle block selection with multi-select support.
   * - Normal click: select only this block
   * - Shift+click: add to / toggle from selection
   */
  const handleBlockSelect = useCallback((blockId, event) => {
    const isShiftClick = event?.shiftKey;

    if (isShiftClick) {
      // Toggle selection with Shift
      setSelectedBlockIds((prev) => {
        const next = new Set(prev);
        if (next.has(blockId)) {
          next.delete(blockId);
        } else {
          next.add(blockId);
        }
        return next;
      });
    } else {
      // Single select
      setSelectedBlockIds(new Set([blockId]));
    }
  }, []);

  const handleBackgroundClick = useCallback((event) => {
    // Only handle clicks directly on the container, not bubbled from children
    if (event.target !== event.currentTarget) {
      return;
    }

    // Don't deselect if we just finished dragging, resizing, or editing
    const CLICK_THRESHOLD_MS = 100;
    if (
      justFinishedDragging(CLICK_THRESHOLD_MS)
      || Date.now() - resizeEndTimeRef.current < CLICK_THRESHOLD_MS
      || Date.now() - editingEndTimeRef.current < CLICK_THRESHOLD_MS
    ) {
      return;
    }

    setSelectedBlockIds(new Set());
    setEditingBlockId(null);
  }, [justFinishedDragging]);

  // Listen for today button clicks from header
  useEffect(() => {
    window.addEventListener('planner:jumpToToday', jumpToToday);
    return () => {
      window.removeEventListener('planner:jumpToToday', jumpToToday);
    };
  }, [jumpToToday]);

  useEffect(() => {
    const handleJumpToWeek = (e) => jumpToTargetWeek(e.detail?.week);
    window.addEventListener('planner:jumpToTargetWeek', handleJumpToWeek);
    return () => {
      window.removeEventListener('planner:jumpToTargetWeek', handleJumpToWeek);
    };
  }, [jumpToTargetWeek]);

  const handleCellClick = (week, row) => {
    // Don't create blocks if we just finished editing
    const CLICK_THRESHOLD_MS = 100;
    const justFinishedEditing = Date.now() - editingEndTimeRef.current < CLICK_THRESHOLD_MS;

    if (
      !isOnline
      || !selectedCalendarId
      || editingBlockId
      || justFinishedEditing
    ) {
      return;
    }

    const existingBlock = blocks.find((block) => {
      const blockFromIndex = weekIndexMap.get(block.fromWeek);
      const blockToIndex = weekIndexMap.get(block.toWeek);
      const clickedWeekIndex = weekIndexMap.get(week);
      return (
        block.row === row
        && clickedWeekIndex >= blockFromIndex
        && clickedWeekIndex <= blockToIndex
      );
    });

    if (existingBlock) return;

    // If blocks are selected, just deselect them instead of creating a new block
    if (selectedBlockIds.size > 0) {
      setSelectedBlockIds(new Set());
      return;
    }

    const { startDate, endDate } = weekToDateRange(week);
    const newBlock = {
      name: '',
      startDate,
      endDate,
      fromWeek: week,
      toWeek: week,
      startWeek: week,
      endWeek: week,
      row,
      colorId: 'sky',
    };

    dispatch(createBlock(newBlock))
      .then((createdBlock) => {
        if (createdBlock?.id) {
          setSelectedBlockIds(new Set([createdBlock.id]));
        }
      })
      .catch(() => {
        // Clear selection on error
        setSelectedBlockIds(new Set());
      });
  };

  if (!loadedWeekRange.fromWeek || !loadedWeekRange.toWeek) {
    return (
      <PlanViewContainer>
        <EmptyGrid>Loading week range...</EmptyGrid>
      </PlanViewContainer>
    );
  }

  const hasRenderedWindow = visibleWindow.end >= visibleWindow.start;
  const renderedWeeks = hasRenderedWindow
    ? weeks.slice(visibleWindow.start, visibleWindow.end + 1)
    : [];
  const renderOffsetPx = visibleWindow.start * CELL_WIDTH;

  return (
    <PlanViewContainer onClick={handleBackgroundClick}>
      {!isOnline && (
        <OfflineBanner>
          You are currently offline. Viewing only - changes cannot be saved.
        </OfflineBanner>
      )}

      <GridViewport ref={gridContainerRef}>
        <GridContainer
          style={{
            width: `${weeks.length * CELL_WIDTH + 12}px`,
          }}
        >
          {(() => {
            const currentWeekIndex = weekIndexMap.get(currentWeekString);
            if (currentWeekIndex !== undefined) {
              return (
                <CurrentWeekIndicator
                  style={{
                    left: `${currentWeekIndex * CELL_WIDTH}px`,
                  }}
                />
              );
            }
            return null;
          })()}
          <WeekHeader
            weeks={weeks}
            renderedWeeks={renderedWeeks}
            currentWeekString={currentWeekString}
            previousYear={previousYear}
            renderOffsetPx={renderOffsetPx}
          />

          <DndContext {...dndContextProps}>
            <GridBody>
              {rows.map((rowNum) => (
                <GridRow key={rowNum}>
                  {renderOffsetPx > 0 && <GridSpacer width={renderOffsetPx} />}
                  {renderedWeeks.map((week) => {
                    const hasBlock = cellHasBlock.has(`${rowNum}-${week}`);

                    return (
                      <GridCell
                        key={`${rowNum}-${week}`}
                        week={week}
                        rowNum={rowNum}
                        isDragging={Object.keys(draggingBlocks).length > 0}
                        hasBlock={hasBlock}
                        isEditing={!!editingBlockId}
                        hasSelection={selectedBlockIds.size > 0}
                        onCellClick={handleCellClick}
                      />
                    );
                  })}
                </GridRow>
              ))}

              <BlocksLayer>
                {visibleBlocks
                  .filter((block) => {
                    if (visibleWindow.end < visibleWindow.start) return false;
                    const fromIndex = weekIndexMap.get(block.fromWeek);
                    const toIndex = weekIndexMap.get(block.toWeek);
                    if (fromIndex === undefined || toIndex === undefined) return false;

                    return !(
                      toIndex < visibleWindow.start
                      || fromIndex > visibleWindow.end
                    );
                  })
                  .map((block) => {
                    const overlappingBlocks = getOverlappingBlocks(block.id);
                    const displayBlock = draggingBlocks[block.id]
                      ? {
                        ...block,
                        ...draggingBlocks[block.id],
                      }
                      : block;
                    const showOverlapIndicator = isRightmostOverlappingBlock(
                      block.id,
                    );
                    const positionProps = blockPositions.get(block.id) || {};

                    return (
                      <PlanBlock
                        key={block.id}
                        block={displayBlock}
                        originalBlock={block}
                        weeks={weeks}
                        weeksLength={weeks.length}
                        cellWidth={CELL_WIDTH}
                        cellHeight={CELL_HEIGHT}
                        rowHeight={ROW_HEIGHT}
                        maxRows={rows.length}
                        selected={selectedBlockIds.has(block.id)}
                        hasOverlap={overlappingBlocks.length > 0}
                        overlappingCount={overlappingBlocks.length}
                        showOverlapIndicator={showOverlapIndicator}
                        isOnline={isOnline}
                        onSelect={handleBlockSelect}
                        onResize={handleBlockResize}
                        onNameChange={handleBlockNameChange}
                        onEditingStart={handleEditingStart}
                        onEditingEnd={handleEditingEnd}
                        onColorChange={handleBlockColorChange}
                        {...positionProps}
                      />
                    );
                  })}
              </BlocksLayer>
            </GridBody>

            <DragOverlay dropAnimation={null}>
              {activeBlock && activeBlockData && (
                <DragOverlayBlock
                  block={activeBlock}
                  width={activeBlockData.weekSpan * CELL_WIDTH - BLOCK_MARGIN * 2}
                  height={CELL_HEIGHT - BLOCK_MARGIN * 2}
                />
              )}
            </DragOverlay>
          </DndContext>
        </GridContainer>
      </GridViewport>

      {/* Horizontal scroll navigation buttons for mouse users */}
      {!isTouchDevice && (
        <>
          <ScrollNavButton
            direction='left'
            onMouseDown={() => startScrolling('left')}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            disabled={!canScrollLeft}
            aria-label='Scroll left'
          >
            <svg viewBox='0 0 24 24'>
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </ScrollNavButton>
          <ScrollNavButton
            direction='right'
            onMouseDown={() => startScrolling('right')}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            disabled={!canScrollRight}
            aria-label='Scroll right'
          >
            <svg viewBox='0 0 24 24'>
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </ScrollNavButton>
        </>
      )}

      <LoadingIndicator show={isLoadingMoreWeeks}>
        Loading more weeks...
      </LoadingIndicator>
    </PlanViewContainer>
  );
};

export default PlanView;
